<template>
  <div class="search-page">
    <div
      class="filter-container"
      :class="{expanded: filterOptionsExpanded}"
    >
      <div 
        class="filter-container-header"
        @click="filterOptionsExpanded = !filterOptionsExpanded"
      >
        Filter Results

        <span :class="filterOptionsExpanded ? 'up-arrow' : 'down-arrow'" />
      </div>
      <div
        class="filter-container-content"
        :class="{
          expanded: filterOptionsExpanded,
          collapsed: !filterOptionsExpanded,
        }"
      >
        <div class="input-group">
          <TextInput
            v-model="searchTerm"
            label="Search Term"
          />
          <Dropdown
            v-model="buildTypeId"
            label="Build Type"
            :options="buildTypeDropdownOptions"
          />
          <CartridgeSearchMachine v-model="cartridgeId" />
          <TextInput
            v-if="customer"
            v-model="customerName"
            :disabled="true"
            label="Customer"
          />
          <CustomerSearchMachine
            v-else
            :show-create-new="false"
            @customer="onCustomerSelected"
          />
          <div class="checkbox-input-group">
            <Checkbox
              v-model="isApproved"
              label="Only Show Approved Quotes"
              class="first-checkbox"
            />
          </div>
        </div>
        <div class="button-wrapper">
          <button
            class="clear"
            @click="clearSearchForm"
          >
            Clear
          </button>
          <button
            class="search"
            @click="search"
          >
            Search
          </button>
        </div>
      </div>
    </div>
    <div class="results-container-header">
      <div class="results-container-header-text">
        Quotes
      </div>
      <router-link
        class="create"
        to="/quote/0"
      >
        Create
      </router-link>
    </div>
    <LoadingPulse
      :loading="loading"
      class="loading-spinner"
    />
    <div
      v-if="!loading"
      class="overflow-wrapper"
    >
      <table>
        <thead>
          <tr>
            <th @click="sortBy('Id')">
              <div>
                Id
                <span
                  v-show="sortField === 'Id'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('BuildType')">
              <div>
                Build Type
                <span
                  v-show="sortField === 'BuildType'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('Cartridge')">
              <div>
                Cartridge
                <span
                  v-show="sortField === 'Cartridge'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('CustomerName')">
              <div>
                Customer Name
                <span
                  v-show="sortField === 'CustomerName'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('Approved')">
              <div>
                Approved
                <span
                  v-show="sortField === 'Approved'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th class="no-sort" />
          </tr>
        </thead>
        <tbody v-if="quotes.length > 0">
          <tr
            v-for="quote in quotes"
            :key="quote.id"
            class="clickable"
            @click="onTableRowClicked(quote.id)"
          >
            <td>{{ quote.id }}</td>
            <td>{{ quote.buildType }}</td>
            <td>{{ quote.cartridge }}</td>
            <td>{{ quote.customerName }}</td>
            <td>{{ quote.approved ? 'Yes' : 'No' }}</td>
            <td class="delete">
              <TrashCanSvg @click.stop="onDeleteClicked(quote.id)" />
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td
              :colspan="6"
              class="message"
            >
              No quotes were found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Paginator
      v-if="!loading"
      class="paginator"
      :total-results="totalResults"
      :page-number="pageNumber"
      :page-size="pageSize"
      @page-change="onPageChanged"
      @page-size-change="onPageSizeChanged"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Paginator from '@/components/shared/Paginator.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { QuoteService } from '@/services/quote.service'
import { Quote } from '@/@types/responses/quote/Quote'
import Dropdown from '@/components/shared/Dropdown.vue'
import { BuildTypeService } from '@/services/buildType.service'
import CustomerSearchMachine from '@/components/elements/CustomerSearchMachine.vue'
import { Customer } from '@/@types/responses/customer/Customer'
import CartridgeSearchMachine from '@/components/elements/CartridgeSearchMachine.vue'
import { BuildTypeDropdownOption } from '@/@types/responses/build_type/BuildTypeDropdownOption'
import Checkbox from '@/components/shared/Checkbox.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Quotes',
  components: {
    CartridgeSearchMachine,
    Checkbox,
    CustomerSearchMachine,
    Dropdown,
    LoadingPulse,
    Paginator,
    TextInput,
    TrashCanSvg
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const quoteService = inject(PROVIDER_CONSTANTS.quoteService) as QuoteService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      buildTypeService,
      quoteService,
      toast
    }
  },
  data() {
    let buildTypeId: number|undefined
    let cartridgeId: number|undefined
    let customer: Customer|undefined

    return {
      ascending: true,
      buildTypeDropdownOptions: [] as BuildTypeDropdownOption[],
      buildTypeId,
      cartridgeId,
      customer,
      filterOptionsExpanded: true,
      isApproved: false,
      loading: true,
      pageNumber: 1,
      pageSize: 10,
      quotes: [] as Quote[],
      searchTerm: '',
      sortField: 'Id',
      totalResults: 0
    }
  },
  computed: {
    customerName(): string {
      if (!this.customer) return ''

      return `${this.customer.firstName} ${this.customer.lastName}`
    }
  },
  mounted() {
    window.addEventListener("popstate", this.parseQueryStringAndSearch)

    this.fetchBuildTypeDropdownOptions().then(() => {
      this.parseQueryStringAndSearch()
    })
  },
  unmounted() {
    window.removeEventListener('popstate', this.parseQueryStringAndSearch)
  },
  methods: {
    clearSearchForm(): void {
      this.buildTypeId = undefined
      this.cartridgeId = undefined
      this.customer = undefined
      this.isApproved = false
      this.searchTerm = ''
      this.search()
    },
    fetchBuildTypeDropdownOptions(): Promise<void> {
      return this.buildTypeService.getBuildTypeDropdownOptions().then(dropdownOptionsResponse => {
        this.buildTypeDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build types. Try reloading the page.')
        }
      })
    },
    getBooleanParameterByName(name: string): boolean | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? parameter === 'true' : undefined
    },
    getNumericParameterByName(name: string): number | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? Number(parameter) : undefined
    },
    getStringParameterByName(name: string): string | undefined {
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
      const results = regex.exec(window.location.href)

      if (!results || !results[2])
        return undefined
      
      return decodeURIComponent(results[2].replace(/\+/g, " "));
    },
    onCustomerSelected(customer: Customer): void {
      this.customer = customer
    },
    onDeleteClicked(id: number): void {
      this.quoteService.deleteQuote(id).then(() => {
        if ((this.quotes.length - 1) === 0 && this.pageNumber > 1)
          this.onPageChanged(this.pageNumber - 1)
        else
          this.searchQuotes()

        this.toast.success('Quote deleted successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the quote. Please try again.')
        }
      })
    },
    onPageChanged(pageNumber: number): void {
      this.pageNumber = pageNumber
      this.searchQuotes()
    },
    onPageSizeChanged(pageSize: number): void {
      this.pageSize = pageSize
      this.search()
    },
    onTableRowClicked(id: number): void {
      this.$router.push('/quote/' + id)
    },
    parseQueryStringAndSearch(): void {
      const ascendingFromUrl = this.getBooleanParameterByName("a")
      this.ascending = ascendingFromUrl !== undefined ? ascendingFromUrl : this.ascending

      const buildTypeIdFromUrl = this.getNumericParameterByName("bt")
      this.buildTypeId = buildTypeIdFromUrl !== undefined ? buildTypeIdFromUrl : this.buildTypeId

      const cartridgeIdFromUrl = this.getNumericParameterByName("ca")
      this.cartridgeId = cartridgeIdFromUrl !== undefined ? cartridgeIdFromUrl : this.cartridgeId

      const customerIdFromUrl = this.getNumericParameterByName("ci")
      const customerNameFromUrl = this.getStringParameterByName("cn")
      if (customerIdFromUrl !== undefined && customerNameFromUrl !== undefined)
        this.customer = new Customer(customerIdFromUrl, customerNameFromUrl.split(' ')[0], customerNameFromUrl.split(' ')[1], '', '', '', '', '', undefined, '')

      const isApprovedFromUrl = this.getBooleanParameterByName("i")
      this.isApproved = isApprovedFromUrl !== undefined ? isApprovedFromUrl : this.isApproved

      const pageNumberFromUrl = this.getNumericParameterByName("pn")
      this.pageNumber = pageNumberFromUrl !== undefined ? pageNumberFromUrl : this.pageNumber

      const pageSizeFromUrl = this.getNumericParameterByName("ps")
      this.pageSize = pageSizeFromUrl !== undefined ? pageSizeFromUrl : this.pageSize

      const searchTermFromUrl = this.getStringParameterByName("st")
      this.searchTerm = searchTermFromUrl !== undefined ? searchTermFromUrl : this.searchTerm

      const sortFieldFromUrl = this.getStringParameterByName("sf")
      this.sortField = sortFieldFromUrl !== undefined ? sortFieldFromUrl : this.sortField

      this.searchQuotes()
    },
    search(): void {
      this.pageNumber = 1
      this.searchQuotes()
    },
    searchQuotes(): void {
      this.loading = true
      this.updateUrl()
      this.quoteService.searchQuotes(this.searchTerm, this.pageNumber, this.pageSize, this.sortField, this.buildTypeId, this.cartridgeId, this.customer?.id, this.isApproved, this.ascending).then(pagingResponse => {
        this.totalResults = pagingResponse.totalResults
        this.quotes = pagingResponse.results
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the quotes. Try reloading the page.')
        }
      })
    },
    sortBy(sortField: string): void {
      if (this.sortField === sortField)
        this.ascending = !this.ascending
      else
        this.sortField = sortField

      this.searchQuotes()
    },
    updateUrl(): void {
      let newQueryString = `?a=${this.ascending}&pn=${this.pageNumber}&ps=${this.pageSize}&sf=${this.sortField}&i=${this.isApproved}`

      if (this.buildTypeId !== undefined)
        newQueryString = newQueryString + `&bt=${this.buildTypeId}`

      if (this.cartridgeId !== undefined)
        newQueryString = newQueryString + `&ca=${this.cartridgeId}`

      if (this.customer !== undefined) {
        newQueryString = newQueryString + `&ci=${this.customer.id}`
        newQueryString = newQueryString + `&cn=${encodeURIComponent(this.customerName)}`
      }

      if (this.searchTerm !== '')
        newQueryString = newQueryString + `&st=${encodeURIComponent(encodeURIComponent(this.searchTerm))}`
      
      this.$router.replace('/quotes' + newQueryString)
    }
  }
})
</script>

<style scoped lang="scss">
.search-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 15px;

  @include sac-search-page;
}
</style>
